import {
  getMerchant,
  deleteMerchant,
  updateMerchant,
  addMerchant,
} from '@/adapters/master';
import { IMerchant, IMerchantRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

const getErrorMessage = (error: any): string => {
  if (error && error.message) return error.message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const MerchantPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IMerchant[]>([]);
  const [selected, setSelected] = useState<IMerchant[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<IMerchantRes, Error>({
    queryKey: ['get-merchant'],
    queryFn: getMerchant,
    retry: 0,
  });

  const notifyBatchErrors = (title: string, res: any, originalData: any[]) => {
    const errors: string[] = [];
    if (Array.isArray(res?.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const code = originalData[index]?.CMAD_ADDR || `Index: ${index}`;
          errors.push(
            `${code}: ${result.reason?.errors?.errorMessage || 'Server Error'}`,
          );
        }
      });
    } else if (res?.status !== 'OK')
      errors.push(res?.message || 'Server Error');

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
      });
      return true;
    }
    return false;
  };

  const actionMerchantMutation = useMutation<
    IApiResponse,
    Error,
    Partial<IMerchant>[]
  >({
    mutationFn: payload =>
      tab === 'create' ? addMerchant(payload) : updateMerchant(payload),
    onSuccess: (res, variables) => {
      const title =
        tab === 'create' ? 'Sync ข้อมูลไม่สำเร็จ' : 'Update ข้อมูลไม่สำเร็จ';
      if (!notifyBatchErrors(title, res, variables)) {
        notification.success({
          message:
            tab === 'create' ? 'Sync ข้อมูลสำเร็จ' : 'Update ข้อมูลสำเร็จ',
        });
        refetch();
      }
    },
    onError: error =>
      notification.error({
        message: 'เกิดข้อผิดพลาด',
        description: getErrorMessage(error),
      }),
  });

  const actionDeleteMerchant = useMutation<
    IApiResponse,
    Error,
    Partial<IMerchant>[]
  >({
    mutationFn: payload => deleteMerchant(payload),
    onSuccess: (res, variables) => {
      if (!notifyBatchErrors('ลบข้อมูลไม่สำเร็จ', res, variables)) {
        notification.success({ message: 'ลบข้อมูลสำเร็จ' });
        refetch();
      }
    },
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.CMAD_ADDR?.toLowerCase().includes(lowerSearch) ||
        item.CMAD_NAME?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<IMerchant> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัสล้ง',
        dataIndex: 'CMAD_ADDR',
        width: 120,
        sorter: (a, b) => (a.CMAD_ADDR || '').localeCompare(b.CMAD_ADDR || ''),
        filters: Array.from(
          new Set(selected.map(i => i.CMAD_ADDR).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.CMAD_ADDR === v,
        filterSearch: true,
      },
      {
        title: 'ชื่อล้ง',
        dataIndex: 'CMAD_NAME',
        sorter: (a, b) => (a.CMAD_NAME || '').localeCompare(b.CMAD_NAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.CMAD_NAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.CMAD_NAME === v,
        filterSearch: true,
      },
      {
        title: 'ตำบล',
        dataIndex: 'CMAD_TUMBOL_NAME',
        width: 150,
        sorter: (a, b) =>
          (a.CMAD_TUMBOL_NAME || '').localeCompare(b.CMAD_TUMBOL_NAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.CMAD_TUMBOL_NAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.CMAD_TUMBOL_NAME === v,
        filterSearch: true,
      },
      {
        title: 'อำเภอ',
        dataIndex: 'CMAD_AMPHUR_NAME',
        width: 150,
        sorter: (a, b) =>
          (a.CMAD_AMPHUR_NAME || '').localeCompare(b.CMAD_AMPHUR_NAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.CMAD_AMPHUR_NAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.CMAD_AMPHUR_NAME === v,
        filterSearch: true,
      },
      {
        title: 'จังหวัด',
        dataIndex: 'CMAD_CITY',
        width: 180,
        sorter: (a, b) => (a.CMAD_CITY || '').localeCompare(b.CMAD_CITY || ''),
        filters: Array.from(
          new Set(selected.map(i => i.CMAD_CITY).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.CMAD_CITY === v,
        filterSearch: true,
      },
    ],
    [selected],
  );

  useEffect(() => {
    const listMap = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const currentList = listMap[tab] || [];
    setSelected(currentList.map(c => ({ ...c, _key: uniqueId('merch-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const isPending =
    actionMerchantMutation.isPending || actionDeleteMerchant.isPending;

  return (
    <div style={{ paddingBottom: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่มล้ง</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (รหัสล้ง / ชื่อล้ง)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteMerchant.mutate(selectedRows)}
            >
              ลบข้อมูลล้ง
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionMerchantMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังส่ง...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<IMerchant>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1000 }}
        sticky={{ offsetHeader: 0 }}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
    </div>
  );
};

export default MerchantPage;
